import axios from "axios";

// Same-origin by default: when REACT_APP_BACKEND_URL is unset/empty (the
// production Docker + Caddy single-domain setup) this resolves to a relative
// "/api", so the browser always calls the exact host that served the page —
// no baked URL to get wrong, no cross-origin/CORS, no mixed-content. In the
// Emergent preview the full URL is provided and used as-is.
const BASE = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "orchestra_token";

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearAuthToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((cfg) => {
  const t = getAuthToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    if (err.response?.status === 401 && !url.includes("/auth/login")) {
      clearAuthToken();
      window.dispatchEvent(new Event("auth:logout"));
    }
    return Promise.reject(err);
  }
);

export const Endpoints = {
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),

  config: () => api.get("/config").then((r) => r.data),
  professions: () => api.get("/professions").then((r) => r.data),
  agents: () => api.get("/agents").then((r) => r.data),
  workstations: () => api.get("/workstations").then((r) => r.data),
  requirements: () => api.get("/requirements").then((r) => r.data),
  skeletons: () => api.get("/skeletons").then((r) => r.data),
  absences: () => api.get("/absences").then((r) => r.data),
  schedule: () => api.get("/schedule").then((r) => r.data),

  seed: () => api.post("/seed").then((r) => r.data),
  solve: () => api.post("/solve").then((r) => r.data),
  recalculate: (freeze_day, freeze_time) =>
    api.post("/recalculate", { freeze_day, freeze_time }).then((r) => r.data),

  createAgent: (b) => api.post("/agents", b).then((r) => r.data),
  updateAgent: (id, b) => api.put(`/agents/${id}`, b).then((r) => r.data),
  setAgentSkeleton: (id, entries) => api.put(`/agents/${id}/skeleton`, { entries }).then((r) => r.data),
  deleteAgent: (id) => api.delete(`/agents/${id}`).then((r) => r.data),
  createProfession: (b) => api.post("/professions", b).then((r) => r.data),
  updateProfession: (id, b) => api.put(`/professions/${id}`, b).then((r) => r.data),
  deleteProfession: (id) => api.delete(`/professions/${id}`).then((r) => r.data),
  createWorkstation: (b) => api.post("/workstations", b).then((r) => r.data),
  updateWorkstation: (id, b) => api.put(`/workstations/${id}`, b).then((r) => r.data),
  deleteWorkstation: (id) => api.delete(`/workstations/${id}`).then((r) => r.data),
  createRequirement: (b) => api.post("/requirements", b).then((r) => r.data),
  updateRequirement: (id, b) => api.put(`/requirements/${id}`, b).then((r) => r.data),
  bulkRequirements: (b) => api.post("/requirements/bulk", b).then((r) => r.data),
  deleteRequirement: (id) => api.delete(`/requirements/${id}`).then((r) => r.data),
  createAbsence: (b) => api.post("/absences", b).then((r) => r.data),
  deleteAbsence: (id) => api.delete(`/absences/${id}`).then((r) => r.data),

  term: () => api.get("/term").then((r) => r.data),
  cloneTerm: (weeks) => api.post("/term/clone", { weeks }).then((r) => r.data),
  toggleTermWeek: (index) => api.post(`/term/week/${index}/toggle`).then((r) => r.data),
  clearTerm: () => api.delete("/term").then((r) => r.data),
};
